import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  DataObject,
  ReportInfo,
  ReportRecord,
  ReportView,
  TagOutput,
  TemplateProjectionMap,
} from '../../models/report.model';
import { PROPERTY_MAP_ADDRESS } from '../summary-view/summary-view.model';
import { MiqMoment } from '@miq/utils';
import { ReportExportViewService } from '../../report-export-view.service';
import { ReportAllRecordsImage } from '../../models/template.model';
import { ReportImagesService } from '../../report-images.service';
import { DynamicLocaleService } from '../../dynamic-locale.service';
import { IconLoaderService } from '../../icon-loader.service';

@Component({
  selector: 'rep-tag-output-view',
  templateUrl: './tag-output-view.component.html',
  styleUrls: ['./tag-output-view.component.scss'],
  standalone: false,
})
export class TagOutputViewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() reportData: ReportInfo;
  @Input() currentView: ReportView;
  @Input() reportLocale: number;
  @Input() columns: { [entity: string]: { [columnField: string]: any } };
  @Input() templateProjectionMap: TemplateProjectionMap;
  @Input() entity: string;

  displayedColumns: string[] = ['FLOOR', 'SQM', 'SQFT'];
  financialTermColumns: string[] = ['occupancyCost', 'perSqft'];
  financialTermsData = [];
  statusAndCond: DataObject;
  property: TagOutput;
  totalProperties: any[] = [];
  showNavigationArrows = false;
  record: ReportRecord[];
  summaryAddressPaths: DataObject;

  constructor(
    private reportExportViewService: ReportExportViewService,
    private reportImagesService: ReportImagesService,
    private localeService: DynamicLocaleService,
    private iconLoaderService: IconLoaderService
  ) {}

  ngOnChanges({
    columns: _columns,
    currentView: _currentView,
    reportLocale: _reportLocale,
    reportData: _reportData,
  }: SimpleChanges) {
    this.totalProperties = this.reportData.tagOutput;
    this.localeService.setReportLocaleId(this.reportLocale);
    this.getAddressMaps();
    this.fetchAdditionalImagesOfReport();
    if (this.totalProperties.length > 1) {
      this.onRecordChange(0);
    }
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  ngOnInit(): void {
    // required by angular-l10n
  }

  navRecordNum(event) {
    this.onRecordChange(event - 1);
  }

  onRecordChange(index) {
    this.property = this.totalProperties[index];
    if (this.entity === 'availability') {
      this.record = this.reportData.records.filter(rec => rec.associatedProperty.entity.id === this.property.id);
    } else {
      this.record = this.reportData.records.filter(rec => rec.data.entity.id === this.property.id);
    }
    this.setStatusAndCond();
    this.setFinancialTermData();
  }

  private setStatusAndCond() {
    if (this.property.statusAndCond) {
      const { statusAndCond } = this.property;

      let readyToOccupy: string = null;
      if (statusAndCond.readyToOccupy != null) {
        const ukDateFormat = 'DD/MM/YYYY';
        readyToOccupy = MiqMoment.format(this.property.statusAndCond.readyToOccupy, ukDateFormat);
      }

      this.statusAndCond = { ...statusAndCond, readyToOccupy };
    }
  }

  private setFinancialTermData() {
    this.financialTermsData = [
      { key: 'template.guideRent', value: this.property.financialTerms.guideRent },
      { key: 'template.businessRate', value: this.property.financialTerms.businessRate },
      { key: 'template.serviceCharge', value: this.property.financialTerms.serviceCharge },
    ];
  }

  private getAddressMaps() {
    this.summaryAddressPaths = {
      postalAddress: this.reportData.entity
        ? PROPERTY_MAP_ADDRESS[this.reportData.entity]
        : PROPERTY_MAP_ADDRESS.property,
    };
  }

  /**
   * this method is used to fetch the additional image id of the report.
   */
  private fetchAdditionalImagesOfReport() {
    this.reportExportViewService.getImages(this.reportData.reportId).subscribe((res: ReportAllRecordsImage) => {
      this.reportImagesService.setReportRecordImages(res);
    });
  }
}
